import { Controller } from 'react-hook-form';
import { Alert, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import {
  companyDocumentoFiscalInputSchema,
  type CompanyDocumentoFiscalFormValues,
} from './schemas';
import {
  makeDocumentoFiscalId,
  type CompanyDocumentoFiscalDto,
  type CompanyDocumentoFiscalInput,
} from './types';

export interface CompanyDocumentoFiscalFormDialogProps {
  open: boolean;
  documento: CompanyDocumentoFiscalDto | null;
  /** Se true, oculta CST/tipoRetencao PIS-COFINS (regra RPS v2.0). */
  simplesNacional?: boolean;
  hooks: {
    useCreate: () => UseMutationResult<
      CompanyDocumentoFiscalDto,
      Error,
      CompanyDocumentoFiscalInput
    >;
    useUpdate: () => UseMutationResult<
      CompanyDocumentoFiscalDto,
      Error,
      { id: string; input: CompanyDocumentoFiscalInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: CompanyDocumentoFiscalFormValues = {
  modelo: 55,
  serie: '',
  descricaoNF: 'T',
  ambienteNF: 2,
  tipoIntegracaoNF: 'INN',
  tributacaoServico: null,
  municipioTributacao: null,
  nbsCodigo: null,
  ibsIndicadorOperacao: null,
  ibsSituacaoTributaria: null,
  ibsClassificacaoTributaria: null,
  cstSituacaoTributariaPisCofins: null,
  tipoRetencaoPisCofins: null,
};

export function CompanyDocumentoFiscalFormDialog({
  open,
  documento,
  simplesNacional = false,
  hooks,
  onClose,
}: CompanyDocumentoFiscalFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    CompanyDocumentoFiscalFormValues,
    CompanyDocumentoFiscalDto,
    CompanyDocumentoFiscalInput,
    string
  >({
    open,
    entity: documento,
    schema: companyDocumentoFiscalInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (d) => ({
      modelo: d.modelo,
      serie: d.serie,
      descricaoNF: d.descricaoNF ?? 'T',
      ambienteNF: d.ambienteNF,
      tipoIntegracaoNF: d.tipoIntegracaoNF ?? '',
      tributacaoServico: d.tributacaoServico,
      municipioTributacao: d.municipioTributacao,
      nbsCodigo: d.nbsCodigo,
      ibsIndicadorOperacao: d.ibsIndicadorOperacao,
      ibsSituacaoTributaria: d.ibsSituacaoTributaria,
      ibsClassificacaoTributaria: d.ibsClassificacaoTributaria,
      cstSituacaoTributariaPisCofins: d.cstSituacaoTributariaPisCofins,
      tipoRetencaoPisCofins: d.tipoRetencaoPisCofins,
    }),
    getId: (d) => makeDocumentoFiscalId(d.modelo, d.serie),
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.empresa.documentosFiscais.toasts.created'),
      updated: t('features.empresa.documentosFiscais.toasts.updated'),
    },
    focusField: undefined,
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.empresa.documentosFiscais.editTitle')
          : t('features.empresa.documentosFiscais.createTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        {/* PK composta — bloqueada na edição */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="modelo"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.documentosFiscais.fields.modelo')}
                type="number"
                fullWidth
                disabled={isEdit}
                value={Number.isFinite(field.value) ? field.value : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? Number.NaN : Number.parseInt(v, 10));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ step: '1', min: 1, inputMode: 'numeric' }}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('features.empresa.documentosFiscais.fields.modeloHelper')
                }
                sx={{ maxWidth: { sm: 200 } }}
              />
            )}
          />
          <TextField
            label={t('features.empresa.documentosFiscais.fields.serie')}
            fullWidth
            disabled={isEdit}
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.serie)}
            helperText={
              form.formState.errors.serie?.message ??
              t('features.empresa.documentosFiscais.fields.serieHelper')
            }
            {...form.register('serie')}
            sx={{ maxWidth: { sm: 240 } }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="descricaoNF"
            render={({ field, fieldState }) => (
              <TextField
                select
                label={t('features.empresa.documentosFiscais.fields.descricaoNF')}
                fullWidth
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('features.empresa.documentosFiscais.fields.descricaoNFHelper')
                }
              >
                <MenuItem value="T">
                  {t('features.empresa.documentosFiscais.descricaoNF.T')}
                </MenuItem>
                <MenuItem value="R">
                  {t('features.empresa.documentosFiscais.descricaoNF.R')}
                </MenuItem>
              </TextField>
            )}
          />

          <Controller
            control={form.control}
            name="ambienteNF"
            render={({ field, fieldState }) => (
              <TextField
                select
                label={t('features.empresa.documentosFiscais.fields.ambienteNF')}
                fullWidth
                value={Number.isFinite(field.value) ? String(field.value) : ''}
                onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10))}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              >
                <MenuItem value="1">
                  {t('features.empresa.documentosFiscais.ambienteNF.producao')}
                </MenuItem>
                <MenuItem value="2">
                  {t('features.empresa.documentosFiscais.ambienteNF.homologacao')}
                </MenuItem>
              </TextField>
            )}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="tipoIntegracaoNF"
            render={({ field, fieldState }) => (
              <TextField
                select
                label={t('features.empresa.documentosFiscais.fields.tipoIntegracaoNF')}
                fullWidth
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('features.empresa.documentosFiscais.fields.tipoIntegracaoNFHelper')
                }
                sx={{ maxWidth: { sm: 260 } }}
              >
                <MenuItem value="">
                  {t('features.empresa.documentosFiscais.fields.tipoIntegracaoNFNone')}
                </MenuItem>
                {(['INN', 'WSN', 'WSE', 'WSM'] as const).map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`features.empresa.documentosFiscais.tipoIntegracaoNF.${code}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            control={form.control}
            name="tributacaoServico"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.documentosFiscais.fields.tributacaoServico')}
                type="number"
                fullWidth
                value={field.value ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? null : Number.parseFloat(v));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ step: '0.01', min: 0, inputMode: 'decimal' }}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('features.empresa.documentosFiscais.fields.tributacaoServicoHelper')
                }
              />
            )}
          />
        </Stack>

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.empresa.documentosFiscais.sections.fiscalAvancado')}
          </Typography>
        </Divider>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.empresa.documentosFiscais.fields.municipioTributacao')}
            fullWidth
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.municipioTributacao)}
            helperText={form.formState.errors.municipioTributacao?.message}
            {...form.register('municipioTributacao')}
          />
          <TextField
            label={t('features.empresa.documentosFiscais.fields.nbsCodigo')}
            fullWidth
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.nbsCodigo)}
            helperText={form.formState.errors.nbsCodigo?.message}
            {...form.register('nbsCodigo')}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.empresa.documentosFiscais.fields.ibsIndicadorOperacao')}
            fullWidth
            inputProps={{ maxLength: 10 }}
            error={Boolean(form.formState.errors.ibsIndicadorOperacao)}
            helperText={form.formState.errors.ibsIndicadorOperacao?.message}
            {...form.register('ibsIndicadorOperacao')}
          />
          <TextField
            label={t('features.empresa.documentosFiscais.fields.ibsSituacaoTributaria')}
            fullWidth
            inputProps={{ maxLength: 10 }}
            error={Boolean(form.formState.errors.ibsSituacaoTributaria)}
            helperText={form.formState.errors.ibsSituacaoTributaria?.message}
            {...form.register('ibsSituacaoTributaria')}
          />
          <TextField
            label={t('features.empresa.documentosFiscais.fields.ibsClassificacaoTributaria')}
            fullWidth
            inputProps={{ maxLength: 10 }}
            error={Boolean(form.formState.errors.ibsClassificacaoTributaria)}
            helperText={form.formState.errors.ibsClassificacaoTributaria?.message}
            {...form.register('ibsClassificacaoTributaria')}
          />
        </Stack>

        {simplesNacional ? (
          <Alert severity="info" variant="outlined">
            {t('features.empresa.documentosFiscais.fields.pisCofinsHiddenBySimples')}
          </Alert>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('features.empresa.documentosFiscais.fields.cstSituacaoTributariaPisCofins')}
              fullWidth
              inputProps={{ maxLength: 2 }}
              error={Boolean(form.formState.errors.cstSituacaoTributariaPisCofins)}
              helperText={
                form.formState.errors.cstSituacaoTributariaPisCofins?.message ??
                t('features.empresa.documentosFiscais.fields.cstSituacaoTributariaPisCofinsHelper')
              }
              {...form.register('cstSituacaoTributariaPisCofins')}
              sx={{ maxWidth: { sm: 200 } }}
            />
            <TextField
              label={t('features.empresa.documentosFiscais.fields.tipoRetencaoPisCofins')}
              fullWidth
              inputProps={{ maxLength: 1 }}
              error={Boolean(form.formState.errors.tipoRetencaoPisCofins)}
              helperText={
                form.formState.errors.tipoRetencaoPisCofins?.message ??
                t('features.empresa.documentosFiscais.fields.tipoRetencaoPisCofinsHelper')
              }
              {...form.register('tipoRetencaoPisCofins')}
              sx={{ maxWidth: { sm: 200 } }}
            />
          </Stack>
        )}
      </Stack>
    </FormDialog>
  );
}
